import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import VideoPlayer from "./VideoPlayer";
import { useAuth } from "../context/AuthContext";
import {
  AsciiHeader,
  AsciiButton,
  AsciiPanel,
  AsciiLoader,
} from "@/components/ui/ascii-theme";

interface VideoMetadata {
  video_id: string;
  original_filename: string;
  file_size: number;
  upload_timestamp: string;
  status: string;
  file_extension: string;
}

const VideoViewer: React.FC = () => {
  const { asset_id } = useParams<{ asset_id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const [videoMetadata, setVideoMetadata] = useState<VideoMetadata | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!asset_id) {
      setError("No video ID provided");
      setLoading(false);
      return;
    }

    fetchVideoMetadata();
  }, [asset_id]);

  const fetchVideoMetadata = async () => {
    if (!asset_id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/videos/${asset_id}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Video not found");
        }
        throw new Error(`Failed to fetch video: ${response.statusText}`);
      }

      const data = await response.json();
      setVideoMetadata(data);
    } catch (err) {
      console.error("Error fetching video metadata:", err);
      setError(err instanceof Error ? err.message : "Failed to load video");
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate("/asset_library");
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (timestamp: string): string => {
    const date = new Date(timestamp);
    const dateStr = date.toLocaleDateString([], {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
    });
    const timeStr = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateStr} ${timeStr}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-black px-2 sm:px-4 py-2 sm:py-4 font-mono text-xs sm:text-sm">
        <div className="w-full max-w-6xl mx-auto">
          <AsciiHeader title="VIDEO VIEWER" subtitle="view • download • share" />
          <div className="flex items-center justify-center h-64">
            <AsciiLoader text="Loading video" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white text-black px-2 sm:px-4 py-2 sm:py-4 font-mono text-xs sm:text-sm">
        <div className="w-full max-w-6xl mx-auto">
          <AsciiHeader title="VIDEO VIEWER" subtitle="view • download • share" />

          {isAuthenticated && (
            <div className="mb-4">
              <AsciiButton variant="default" onClick={handleGoBack}>
                ← BACK TO LIBRARY
              </AsciiButton>
            </div>
          )}

          <div className="border-2 border-red-600 p-3 sm:p-4 bg-red-50">
            <div className="space-y-3">
              <span className="text-xs sm:text-sm font-bold text-red-600">
                ✗ ERROR: {error}
              </span>
              <AsciiButton variant="default" size="sm" onClick={fetchVideoMetadata}>
                ⟳ RETRY
              </AsciiButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black px-2 sm:px-4 py-2 sm:py-4 font-mono text-xs sm:text-sm">
      <div className="w-full max-w-6xl mx-auto space-y-4 sm:space-y-6">
        {/* ASCII Header */}
        <AsciiHeader title="VIDEO VIEWER" subtitle="view • download • share" />

        {/* Back Button - Only for authenticated users */}
        {isAuthenticated && (
          <div className="mb-4">
            <AsciiButton variant="default" onClick={handleGoBack}>
              ← BACK TO LIBRARY
            </AsciiButton>
          </div>
        )}

        {/* Video Player */}
        <div className="flex justify-center">
          <VideoPlayer
            videoId={videoMetadata?.video_id}
            originalFilename={videoMetadata?.original_filename}
            onError={(error) => setError("Failed to load video player")}
          />
        </div>

        {/* Video Information */}
        {videoMetadata && (
          <AsciiPanel title="═══ VIDEO INFORMATION ═══">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs">
              <div className="space-y-1">
                <p className="uppercase opacity-60 font-bold">└─ Filename</p>
                <p className="pl-4">{videoMetadata.original_filename}</p>
              </div>
              <div className="space-y-1">
                <p className="uppercase opacity-60 font-bold">└─ File Size</p>
                <p className="pl-4">{formatFileSize(videoMetadata.file_size)}</p>
              </div>
              <div className="space-y-1">
                <p className="uppercase opacity-60 font-bold">└─ Upload Date</p>
                <p className="pl-4">{formatDate(videoMetadata.upload_timestamp)}</p>
              </div>
              <div className="space-y-1">
                <p className="uppercase opacity-60 font-bold">└─ Format</p>
                <p className="pl-4">{videoMetadata.file_extension.toUpperCase()}</p>
              </div>
              <div className="space-y-1">
                <p className="uppercase opacity-60 font-bold">└─ Status</p>
                <p className="pl-4 capitalize">{videoMetadata.status}</p>
              </div>
              <div className="space-y-1">
                <p className="uppercase opacity-60 font-bold">└─ Video ID</p>
                <p className="pl-4 font-mono text-[0.65rem]">
                  {videoMetadata.video_id}
                </p>
              </div>
            </div>
          </AsciiPanel>
        )}

        {/* Footer */}
        <div className="mt-8 pt-4 border-t-2 border-black">
          <pre className="text-center text-[0.65rem] sm:text-xs opacity-60">
            {`─────────────────────────────────────────────────────────────────
Built by Max Maio | max@midship.ai | midship.ai
─────────────────────────────────────────────────────────────────`}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default VideoViewer;
